import {
  FulfillmentOrderLineItem,
  getLineItemGroupingKey,
  groupFulfillmentOrderLineItems,
} from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import type { InventoryLevelId } from "@redotech/redo-warehouse-id";

export function groupProblemPickLineItems({
  items,
  pickedQuantityByExternalId,
  degroupByCustomAttributes,
}: {
  items: FulfillmentOrderLineItem[];
  pickedQuantityByExternalId: Map<string, number>;
  degroupByCustomAttributes: boolean;
}): ProblemPickLineItemGroup[] {
  const groups = new Map<string, FulfillmentOrderLineItem[]>();
  const groupingConfig = { degroupByCustomAttributes };

  for (const item of items) {
    const key = JSON.stringify([
      getLineItemGroupingKey(item, groupingConfig),
      item.imsInventoryItemId ?? null,
    ]);
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      groups.set(key, [item]);
    }
  }

  return [...groups.values()].flatMap((originalItems) => {
    const item = groupFulfillmentOrderLineItems(
      originalItems,
      groupingConfig,
    )[0];
    if (!item) {
      return [];
    }
    return [
      {
        item,
        originalItems,
        pickedQuantity: originalItems.reduce(
          (total, originalItem) =>
            total +
            (pickedQuantityByExternalId.get(originalItem.externalId) ?? 0),
          0,
        ),
      },
    ];
  });
}

export function takeProblemPickSources({
  sources,
  quantity,
}: {
  sources: ProblemPickSource[];
  quantity: number;
}): ProblemPickSourceAllocation {
  const selectedSources: ProblemPickSource[] = [];
  const remainingSources: ProblemPickSource[] = [];
  let missingQuantity = quantity;

  for (const source of sources) {
    const quantityToTake = Math.min(source.quantity, missingQuantity);

    if (quantityToTake > 0) {
      selectedSources.push({ ...source, quantity: quantityToTake });
      missingQuantity -= quantityToTake;
    }

    const quantityRemaining = source.quantity - quantityToTake;
    if (quantityRemaining > 0) {
      remainingSources.push({ ...source, quantity: quantityRemaining });
    }
  }

  return { selectedSources, remainingSources, missingQuantity };
}

export function getProblemPickInventoryIdentity(
  inventoryItemId: string | null | undefined,
): string {
  return inventoryItemId
    ? `Item: ${inventoryItemId.slice(-8)}`
    : "Pooled inventory";
}

export type ProblemPickSource =
  | {
      type: "inventoryLevel";
      inventoryLevelId: InventoryLevelId;
      quantity: number;
    }
  | { type: "external"; quantity: number };

interface ProblemPickSourceAllocation {
  selectedSources: ProblemPickSource[];
  remainingSources: ProblemPickSource[];
  missingQuantity: number;
}

interface ProblemPickLineItemGroup {
  item: FulfillmentOrderLineItem;
  originalItems: FulfillmentOrderLineItem[];
  pickedQuantity: number;
}
